import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toJS } from 'mobx';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import { orderBy } from 'lodash';
import Select from './Select';
import FetchService from '../services/FetchService';

const FetchSelectV2 = (props) => {
  const {
    allowClear,
    url,
    label,
    resultTransformer,
    resultFilter,
    placeholder,
    errorText,
    value,
    onChange,
    disabled,
    defaultSelect,
    required,
    showSearch,
    multiple,
    params,
    sortCondition,
    formItemStyle,
    apiVersion,
    defaultOption,
    page,
    size,
  } = props;

  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [filter, setFilter] = useState(null);

  const search = useCallback(
    (currentFilter) => {
      if (!url) return;
      setFetching(true);
      const fetchService = new FetchService();
      fetchService
        .fetch({
          url: `/${url}`,
          apiVersion,
          query: {
            page,
            size,
            ...(currentFilter && {
              [resultTransformer.text]: {
                $regex: currentFilter,
                $options: 'i',
              },
            }),
            ...params,
          },
        })
        .then(() => {
          setFetching(false);
          if (!fetchService.data) return;

          const docs = toJS(fetchService.data.docs || []);
          const filteredData = orderBy(docs.filter(resultFilter), sortCondition || resultTransformer.text);

          setData(filteredData);

          if (defaultSelect) {
            if (defaultOption) {
              onChange && onChange(get(defaultOption, resultTransformer.value) || null);
            } else {
              onChange && onChange(get(filteredData[0], resultTransformer.value) || null);
            }
          }
        });
    },
    [
      url,
      apiVersion,
      page,
      size,
      resultTransformer,
      params,
      resultFilter,
      sortCondition,
      defaultSelect,
      defaultOption,
      onChange,
    ]
  );

  useEffect(() => {
    search(filter);
  }, [url, JSON.stringify(params)]);

  const requestedIdsRef = useRef(new Set());

  useEffect(() => {
    requestedIdsRef.current = new Set();
  }, [url]);

  useEffect(() => {
    const selectedIds = multiple ? value || [] : value ? [value] : [];
    if (!url || !selectedIds.length) return;

    const missingIds = selectedIds.filter(
      (id) => !requestedIdsRef.current.has(id) && !data.find((item) => get(item, resultTransformer.value) === id)
    );
    if (!missingIds.length) return;

    missingIds.forEach((id) => requestedIdsRef.current.add(id));

    Promise.all(
      missingIds.map((id) => {
        const fetchService = new FetchService();
        return fetchService.fetch({ url: `/${url}/${id}`, apiVersion }).then(() => fetchService.data);
      })
    ).then((results) => {
      const fetchedItems = results.filter(Boolean);
      if (!fetchedItems.length) return;

      setData((prev) => {
        const newItems = fetchedItems.filter(
          (item) =>
            !prev.find((existing) => get(existing, resultTransformer.value) === get(item, resultTransformer.value))
        );
        return newItems.length ? orderBy([...prev, ...newItems], sortCondition || resultTransformer.text) : prev;
      });
    });
  }, [url, value, data, multiple, apiVersion, resultTransformer.value, sortCondition]);

  const searchRef = useRef(search);
  searchRef.current = search;

  const handleSearch = useMemo(
    () =>
      debounce((text) => {
        setFilter(text);
        searchRef.current(text);
      }, 500),
    []
  );

  useEffect(() => () => handleSearch.cancel(), [handleSearch]);

  return (
    <Select
      allowClear={allowClear}
      mode={multiple ? 'multiple' : 'default'}
      required={required}
      value={value}
      label={label}
      showSearch={showSearch}
      disabled={disabled}
      errorText={errorText}
      placeholder={placeholder}
      onChange={(val) =>
        onChange &&
        onChange(
          val,
          data.find((item) => get(item, resultTransformer.value) === val)
        )
      }
      options={[
        ...(defaultOption ? [defaultOption] : []),
        ...data.map((option) => ({
          label: resultTransformer.textFunc ? resultTransformer.textFunc(option) : get(option, resultTransformer.text),
          value: get(option, resultTransformer.value),
        })),
      ]}
      filterOption={(val, option) => option.props.children.toLowerCase().indexOf(val.toLowerCase()) > -1}
      onSearch={handleSearch}
      loading={fetching}
      formItemStyle={formItemStyle}
    />
  );
};

FetchSelectV2.propTypes = {
  allowClear: PropTypes.bool,
  url: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  resultTransformer: PropTypes.shape({
    text: PropTypes.string.isRequired,
    textFunc: PropTypes.func,
    value: PropTypes.string,
  }).isRequired,
  resultFilter: PropTypes.func,
  placeholder: PropTypes.string,
  errorText: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  defaultSelect: PropTypes.bool,
  required: PropTypes.bool,
  showSearch: PropTypes.bool,
  multiple: PropTypes.bool,
  params: PropTypes.object,
  sortCondition: PropTypes.arrayOf(PropTypes.string),
  formItemStyle: PropTypes.object,
  apiVersion: PropTypes.string,
  defaultOption: PropTypes.object,
  page: PropTypes.number,
  size: PropTypes.number,
};

FetchSelectV2.defaultProps = {
  allowClear: true,
  placeholder: 'Select...',
  value: null,
  errorText: null,
  disabled: false,
  defaultSelect: false,
  required: false,
  multiple: false,
  showSearch: false,
  params: {},
  resultFilter: () => true,
  sortCondition: null,
  formItemStyle: {},
  page: 1,
  size: 20,
};

export default FetchSelectV2;
